import { CheckIcon } from "@heroicons/react/outline"
import React from "react"

/*
 * Order progress built from the order's real state: `status`, `is_paid`, `created_at` and
 * `status_history` ([{status, at}], one entry per status change, plus "PAID").
 * Orders placed before status history existed have no entries: their steps still tick from the
 * current status, "Order Placed" is dated from created_at, and other steps show no date.
 */

const PROGRESS = ["PENDING", "PACKING", "PICKING_UP", "DELIVERED"]

const dateFormat = new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Dhaka", day: "2-digit", month: "short", year: "numeric" })
const timeFormat = new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Dhaka", hour: "2-digit", minute: "2-digit", hour12: false })

export function formatOrderDate(value) {
	const date = value ? new Date(value) : null
	if (!date || isNaN(date)) return null
	return `${dateFormat.format(date)} - ${timeFormat.format(date)}`
}

/* when the order (last) entered a status, from its history */
function reachedAt(order, status) {
	const entries = (order.status_history || []).filter((entry) => entry.status === status)
	const at = entries.length ? entries[entries.length - 1].at : null
	return at || (status === "PENDING" ? order.created_at : null)
}

function withState(steps) {
	// the next step still to happen is "current" (payment can complete out of order)
	const current = steps.find((s) => !s.done)
	return steps.map((s) => ({ ...s, state: s.done ? "complete" : s === current ? "current" : "upcoming" }))
}

export function orderTimelineSteps(order) {
	const status = order.status || "PENDING"
	const cashOnDelivery = !order.is_paid && order.payment_method === "cash"
	const payment = {
		key: "payment",
		label: cashOnDelivery ? "Cash On Delivery" : order.is_paid ? "Payment Confirmed" : "Payment Pending",
		image: "/images/order/payment.png",
		// cash is collected by the courier on delivery
		done: order.is_paid || (cashOnDelivery && status === "DELIVERED"),
		at: reachedAt(order, "PAID") || (cashOnDelivery ? reachedAt(order, "DELIVERED") : null),
	}
	const placed = { key: "placed", label: "Order Placed", image: "/images/order/placed.png", done: true, at: reachedAt(order, "PENDING") }

	if (status === "CANCELLED" || status === "REFUNDED") {
		const ended = {
			key: "ended",
			label: status === "CANCELLED" ? "Order Cancelled" : "Order Refunded",
			image: "/images/order/cancelOrder.png",
			done: true,
			at: reachedAt(order, status),
		}
		return withState(order.is_paid ? [placed, payment, ended] : [placed, ended])
	}

	const reached = Math.max(PROGRESS.indexOf(status), 0)
	// a step's label once the order is past it, while it is the current status, and before it
	const step = (key, [past, now, ahead], image, index) => ({
		key,
		label: reached > index ? past : reached === index ? now : ahead,
		image,
		done: reached >= index,
		at: reached >= index ? reachedAt(order, PROGRESS[index]) : null,
	})
	return withState([
		placed,
		payment,
		step("packing", ["Package Prepared", "Preparing Package", "Preparing Package"], "/images/order/package.png", 1),
		step("shipping", ["Shipped", "On The Way", "Yet To Ship"], "/images/order/shipped.png", 2),
		step("delivery", ["Delivered", "Delivered", "Yet To Deliver"], "/images/order/delivery.png", 3),
	])
}

export default function OrderTimeline({ order }) {
	if (!order || !order.order_id) return null
	const steps = orderTimelineSteps(order)

	return (
		<nav aria-label='Order progress'>
			<ol className='grid gap-x-2' style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
				{steps.map((step, index) => {
					const date = formatOrderDate(step.at)
					const next = steps[index + 1]
					return (
						<li key={step.key} className='relative flex flex-col items-center text-center space-y-2'>
							{next && (
								<div
									className={`absolute top-4 left-1/2 h-0.5 ${next.done ? "bg-[#3076CE]" : "bg-gray-200"}`}
									style={{ width: "calc(100% + 0.5rem)" }}
									aria-hidden='true'
								/>
							)}
							{step.state === "complete" ? (
								<div className='relative flex h-8 w-8 items-center justify-center rounded-full bg-[#3076CE]'>
									<CheckIcon className='h-5 w-5 text-white' aria-hidden='true' />
								</div>
							) : step.state === "current" ? (
								<div className='relative flex h-8 w-8 items-center justify-center rounded-full border-2 border-[#3076CE] bg-white' aria-current='step'>
									<span className='h-2.5 w-2.5 rounded-full bg-[#3076CE]' aria-hidden='true' />
								</div>
							) : (
								<div className='relative flex h-8 w-8 items-center justify-center rounded-full border-2 border-gray-300 bg-white' />
							)}
							<p className={`text-xs sm:text-base font-medium ${step.state === "upcoming" ? "text-gray-400" : "text-[#3076CE]"}`}>{step.label}</p>
							<p className='text-gray-500 text-xs sm:text-sm min-h-[1rem] sm:min-h-[1.25rem]'>{date || ""}</p>
							<figure>
								<img
									className={`h-8 sm:h-16 w-8 sm:w-16 object-contain mx-auto ${step.state === "upcoming" ? "opacity-40" : ""}`}
									src={step.image}
									alt=''
								/>
							</figure>
						</li>
					)
				})}
			</ol>
		</nav>
	)
}
